import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LayersOffOutlinedIcon from '@mui/icons-material/LayersClearOutlined';
import { type ImportedLocation } from 'api/location-importer.api';
import { type GradeRecord } from 'views/DispatchImporters/GradeImporterView';
import {
  GRADE_CATEGORY_COLORS,
  GRADE_ZONE_COLOR,
} from './roadsImporter.constants';
import { gradeCategoryColorHex } from './roadsImporter.helpers';

interface UnmapGradeBlocksDialogProps {
  open: boolean;
  location: ImportedLocation | null;
  grades: GradeRecord[];
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}

export function UnmapGradeBlocksDialog({
  open,
  location,
  grades,
  onCancel,
  onConfirm,
}: UnmapGradeBlocksDialogProps): JSX.Element {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await onConfirm();
    } finally {
      setSaving(false);
    }
  };

  const fallbackColor = GRADE_CATEGORY_COLORS.other ?? GRADE_ZONE_COLOR;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: (t) => alpha(t.palette.warning.main, 0.25),
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          py: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          <Typography variant="h6" fontWeight={700}>
            {t('roads_importer_unmap_grade_blocks_title')}
          </Typography>
        </Box>
        <IconButton onClick={onCancel} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 0, pb: 2 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
          {t('roads_importer_unmap_grade_blocks_body', {
            count: grades.length,
            name: location?.name ?? '',
          })}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {grades.map((grade) => {
            const color = gradeCategoryColorHex(
              grade.gradeCategory,
              fallbackColor
            );
            return (
              <Box
                key={grade.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 0.5,
                  px: 1,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {grade.name}
                </Typography>
                {grade.gradeCategory && (
                  <Chip
                    size="small"
                    label={grade.gradeCategory}
                    sx={{ bgcolor: alpha(color, 0.15), color, fontWeight: 600 }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="text" onClick={onCancel} sx={{ fontWeight: 600 }}>
          {t('roads_importer_unmap_grade_blocks_cancel')}
        </Button>
        <Button
          variant="contained"
          color="warning"
          disabled={saving}
          startIcon={
            saving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <LayersOffOutlinedIcon />
            )
          }
          onClick={() => {
            void handleConfirm();
          }}
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('roads_importer_unmap_grade_blocks_confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
